/* Fifth Sense pin code checker (blocks/fs-pdp-pincode.liquid). The request and its handling are the
   live theme's assets/pincode-checker.js: POST { deliveryPincode } to the worker set in the block,
   which alone talks to Eshopbox. The result is drawn as Figma component 952:7725 draws it: an icon,
   the message, and on success the delivery date in bold. */
(function () {
  if (customElements.get('fs-pincode')) return;

  const PIN_RE = /^[1-9][0-9]{5}$/; // Indian PIN: 6 digits, first never 0
  const REQUEST_TIMEOUT_MS = 12000;

  class FsPincode extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-pincode-input]');
      this.button = this.querySelector('[data-pincode-check]');
      this.buttonLabel = this.querySelector('[data-button-label]');
      this.spinner = this.querySelector('[data-spinner]');
      this.help = this.querySelector('[data-help]');
      this.result = this.querySelector('[data-result]');
      if (!this.input || !this.button || !this.result) return;
      // The result replaces the help line; announce it, an error included.
      this.result.setAttribute('aria-live', 'polite');
      this.result.setAttribute('role', 'status');

      this.endpoint = this.dataset.endpoint || '';
      this.format = this.dataset.format === 'days' ? 'days' : 'date';
      this.controller = null;

      this.button.addEventListener('click', () => this.handleSubmit());
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); this.handleSubmit(); }
      });
      this.input.addEventListener('input', () => {
        const cleaned = this.input.value.replace(/\D/g, '').slice(0, 6);
        if (cleaned !== this.input.value) this.input.value = cleaned;
        this.input.setAttribute('aria-invalid', 'false');
        if (cleaned === '') this.clearResult();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async handleSubmit() {
      if (this.loading) return;
      const pincode = (this.input.value || '').trim();
      if (!PIN_RE.test(pincode)) {
        this.input.setAttribute('aria-invalid', 'true');
        this.input.focus();
        this.show('no', this.dataset.msgInvalid || 'Invalid pincode', 'invalid');
        return;
      }
      if (!this.endpoint) {
        this.show('no', this.dataset.msgError || "Couldn't check right now. Please try again.", 'error');
        return;
      }

      this.setLoading(true);
      this.controller?.abort();
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const res = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ deliveryPincode: pincode }),
          signal: this.controller.signal,
        });
        let data = {};
        try { data = await res.json(); } catch { /* tolerate empty or non-JSON bodies */ }

        if (res.status === 400 || data.error === 'INVALID_PINCODE') {
          this.input.setAttribute('aria-invalid', 'true');
          this.show('no', this.dataset.msgInvalid || 'Invalid pincode', 'invalid');
        } else if (res.ok && data.available === true) {
          this.show('ok', this.dataset.msgAvailable || 'Estimated Delivery:', 'success', this.formatEta(data));
        } else if (res.ok && data.available === false) {
          this.show('no', this.dataset.msgUnavailable || 'Delivery not available', 'failure');
        } else {
          this.show('no', this.dataset.msgError || "Couldn't check right now. Please try again.", 'error');
        }
      } catch {
        this.show('no', this.dataset.msgError || "Couldn't check right now. Please try again.", 'error');
      } finally {
        clearTimeout(timeout);
        this.setLoading(false);
      }
    }

    setLoading(on) {
      this.loading = on;
      this.button.disabled = on;
      this.button.setAttribute('aria-busy', String(on));
      if (this.buttonLabel) this.buttonLabel.hidden = on;
      if (this.spinner) this.spinner.hidden = !on;
    }

    show(icon, message, state, date) {
      const tpl = this.querySelector(`template[data-icon="${icon}"]`);
      this.result.replaceChildren();
      if (tpl) this.result.append(tpl.content.cloneNode(true));
      const text = document.createElement('span');
      text.textContent = message;
      this.result.append(text);
      if (date) {
        const strong = document.createElement('strong');
        strong.className = 'fs-pdp-pincode__date';
        strong.textContent = date;
        this.result.append(strong);
      }
      this.result.className = 'fs-pdp-pincode__result fs-pdp-pincode__result--' + state;
      this.result.hidden = false;
      if (this.help) this.help.hidden = true;
    }

    clearResult() {
      this.result.hidden = true;
      this.result.replaceChildren();
      if (this.help) this.help.hidden = false;
    }

    formatEta(data) {
      if (this.format === 'days' && Number.isInteger(data.etdDays)) {
        if (data.etdDays <= 0) return 'Today';
        if (data.etdDays === 1) return 'Tomorrow';
        return `in ${data.etdDays} days`;
      }
      return (data.etd || '').trim();
    }
  }

  customElements.define('fs-pincode', FsPincode);
})();
